import { api } from '@/shared/api/http'
import type { PageResponse } from '@/features/billing/proforma/types'

/** Mirrors the backend ReceivableDtos. */

export type Stage = 'ALL' | 'UNDELIVERED' | 'OVERDUE' | 'IN_COLLECTION' | 'PAID'
export type Scheme = 'STANDARD' | 'DANANTARA'
export type EmailStatus = 'NONE' | 'QUEUED' | 'SENT' | 'FAILED'
export type ShipStatus = 'NONE' | 'IN_TRANSIT' | 'DELIVERED' | 'RETURNED'
export type Relation = 'BERELASI' | 'DAM' | 'NON_DAM' | 'PIHAK_KETIGA'
export type StatusCode = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K'
export type ActionType =
  | 'COVER_LETTER'
  | 'CONFIRMATION_1'
  | 'CONFIRMATION_2'
  | 'BILLING_LETTER'
  | 'WARNING_LETTER'
  | 'SOMATION'
  | 'VISIT'
  | 'STATUS_CHANGE'
export type LetterKind = 'COVER_LETTER' | 'CONFIRMATION_1' | 'CONFIRMATION_2'
export type DocumentCategory =
  | 'INVOICE'
  | 'RECEIPT'
  | 'TAX_INVOICE'
  | 'WHT_SLIP'
  | 'CONTRACT'
  | 'DELIVERY_RECEIPT'
  | 'TRANSFER_PROOF'
  | 'STATUS_EVIDENCE'
  | 'BILLING_LETTER'
  | 'WARNING_LETTER'
  | 'SOMATION'
  | 'VISIT_REPORT'
  | 'OTHER'

export interface ReceivableRow {
  id: number
  invoiceNo: string
  sourceErp: string
  invoiceDate: string | null
  dueDate: string | null
  branchId: string | null
  branchName: string | null
  customerCode: string | null
  customerName: string | null
  groupName: string | null
  relation: Relation | null
  currency: string
  invoiceAmt: number
  paidAmt: number
  outstandingAmt: number
  paymentStatus: string
  ageDays: number | null
  overdueDays: number
  emailStatus: EmailStatus
  emailAt: string | null
  emailError: string | null
  shipStatus: ShipStatus
  shipAt: string | null
  shipCourier: string | null
  shipNote: string | null
  confirmLevel: number
  lastActionType: ActionType | null
  lastActionDate: string | null
  statusCode: StatusCode | null
}

export interface AgingBand {
  key: string
  label: string
  count: number
  amount: number
}

export type TodoKind = 'EMAIL_FAILED' | 'CONFIRMATION_READY' | 'EMAIL_NONE' | 'SHIP_NONE' | 'SHIP_RETURNED'

export interface InvoiceRef {
  id: number
  invoiceNo: string
}

export interface TodoItem {
  kind: TodoKind
  count: number
  invoices: InvoiceRef[]
}

export interface Summary {
  openCount: number
  openAmount: number
  overdueCount: number
  overdueAmount: number
  oldestOverdueDays: number
  undeliveredCount: number
  inCollectionCount: number
  inCollectionAmount: number
  paidCount: number
  totalCount: number
  foreignCount: number
  standard: AgingBand[]
  danantara: AgingBand[]
  todo: TodoItem[]
  lastSyncAt: string | null
}

export interface ClientInfo {
  code: string | null
  name: string | null
  npwp: string | null
  address: string | null
  masterEmail: string | null
  billingEmail: string | null
  emails: string[]
  groupName: string | null
  relation: Relation | null
  picName: string | null
  picPhone: string | null
  inMaster: boolean
}

/** Each field: why that letter cannot be sent now, or null when it can. */
export interface Eligibility {
  coverLetter: string | null
  confirmation1: string | null
  confirmation2: string | null
}

export interface ShipmentEvent {
  id: number
  eventAt: string
  status: ShipStatus
  description: string
  source: string
  recordedBy: string
}

export interface Shipment {
  id: number
  courier: string
  serviceName: string | null
  trackingNo: string | null
  handedOverOn: string
  contents: string | null
  status: ShipStatus
  receivedBy: string | null
  events: ShipmentEvent[]
}

export interface InvoiceAction {
  id: number
  type: ActionType
  date: string
  channel: 'EMAIL' | 'MANUAL'
  status: 'QUEUED' | 'SENT' | 'FAILED' | 'RECORDED'
  letterNo: string | null
  recipientTo: string | null
  note: string | null
  error: string | null
  createdBy: string
  createdAt: string
  /** The action this one sent again, unchanged; null for an original. */
  resendOf: number | null
  /** Whether the exact email that went out can be opened again. */
  hasLetter: boolean
}

/** A letter as it was sent: the copy the client received. */
export interface SentLetter {
  actionId: number
  type: ActionType
  letterNo: string | null
  status: InvoiceAction['status']
  subject: string
  to: string[]
  cc: string[]
  replyTo: string | null
  queuedAt: string
  sentAt: string | null
  error: string | null
  html: string
  attachments: string[]
  invoices: InvoiceRef[]
  /** The letter's online document link; null for a letter sent without one. */
  link: LetterLink | null
}

export type LinkState = 'ACTIVE' | 'EXPIRED' | 'REVOKED'

/** The online copy of a letter, as the officers see it. */
export interface LetterLink {
  url: string
  state: LinkState
  expiresAt: string
  revokedAt: string | null
  revokeReason: 'MANUAL' | 'RESENT' | null
  viewCount: number
  firstViewedAt: string | null
  lastViewedAt: string | null
  /** When the client said, through the link, that the paper documents arrived. */
  receivedAt: string | null
  receivedBy: string | null
  events: LetterLinkEvent[]
}

export interface LetterLinkEvent {
  at: string
  type: 'VIEW' | 'DOWNLOAD' | 'RECEIVED'
  via: 'EMAIL' | 'QR' | null
  detail: string | null
  /** "Chrome di Windows", roughly; null when the browser did not say. */
  device: string | null
}

export interface DocumentFile {
  id: number
  name: string
  category: DocumentCategory | null
  mimeType: string
  sizeBytes: number
  uploadedAt: string
  uploadedBy: string
}

export interface PaymentNote {
  id: number
  paidOn: string
  amount: number
  channel: string
  note: string | null
  createdBy: string
  createdAt: string
}

export interface HistoryEntry {
  at: string
  who: string
  text: string
}

export interface ReceivableDetail {
  row: ReceivableRow
  vaNumber: string | null
  proformaId: number | null
  client: ClientInfo
  letters: Eligibility
  shipments: Shipment[]
  actions: InvoiceAction[]
  documents: DocumentFile[]
  payments: PaymentNote[]
  history: HistoryEntry[]
}

export interface Skipped {
  invoiceId: number
  invoiceNo: string
  reason: string
}

/** One line of a client's checklist in the letter dialog. */
export interface Candidate {
  id: number
  invoiceNo: string
  invoiceDate: string | null
  dueDate: string | null
  currency: string
  outstanding: number
  ageDays: number | null
  overdueDays: number
  included: boolean
}

/** One email to one client. `invoices` empty means the client is left out. */
export interface LetterDraft {
  customerCode: string | null
  customerName: string
  candidates: Candidate[]
  invoices: InvoiceRef[]
  total: number
  to: string[]
  cc: string[]
  subject: string
  html: string
  attachments: DocumentFile[]
  /** Every file of the ticked invoices, to choose attachments from. */
  available: DocumentFile[]
  letterNo: string | null
  problem: string | null
  /** Where the client's reply goes, from Pengaturan surat. */
  replyTo: string | null
}

export interface LetterPreview {
  letters: LetterDraft[]
  skipped: Skipped[]
}

export interface LetterRequest {
  kind: LetterKind
  /** Where the officer started: decides which clients get a letter. */
  invoiceIds: number[]
  /** The ticked invoices across those clients; omit for the defaults. */
  selectedInvoiceIds?: number[]
  letterNos?: Record<string, string>
  to?: string[]
  subject?: string
  attachmentIds?: number[]
  requestId?: string
  /** Adds the online document link and its QR to the email; on unless switched off. */
  includeLink?: boolean
  /** Replaces the default CC for this one letter; an empty list sends without CC. */
  cc?: string[]
}

export interface LetterResult {
  letters: number
  invoices: number
  skipped: Skipped[]
}

export interface ListParams {
  stage?: Stage
  scheme?: Scheme
  bucket?: string
  branchId?: string
  relation?: Relation
  statusCode?: StatusCode
  year?: string
  q?: string
  page?: number
  size?: number
}

export interface ShipmentRequest {
  invoiceIds: number[]
  courier: string
  serviceName?: string
  handedOverOn: string
  contents?: string
  trackingNos?: Record<number, string>
}

export interface ShipmentStatusRequest {
  status: Exclude<ShipStatus, 'NONE'>
  eventAt?: string
  description: string
  receivedBy?: string
}

export interface PaymentNoteRequest {
  paidOn: string
  amount: number
  channel: string
  note?: string
}

export interface ClientProfileRequest {
  groupName?: string | null
  relation?: Relation | null
  billingEmail?: string | null
  picName?: string | null
  picPhone?: string | null
}

export interface UploadRequest {
  category: DocumentCategory
  files: File[]
  documentDate?: string
  letterNo?: string
  note?: string
  statusCode?: StatusCode
}

export const receivableKeys = {
  all: ['receivables'] as const,
  list: (key: string) => ['receivables', 'list', key] as const,
  summary: ['receivables', 'summary'] as const,
  detail: (id: number) => ['receivables', 'detail', id] as const,
  groups: ['receivables', 'groups'] as const,
}

export function fetchReceivables(params: ListParams) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === 'ALL') continue
    search.set(key, String(value))
  }
  if (!search.has('page')) search.set('page', '0')
  return api.get<PageResponse<ReceivableRow>>(`/receivables?${search.toString()}`)
}

export function fetchSummary() {
  return api.get<Summary>('/receivables/summary')
}

export function fetchReceivable(id: number) {
  return api.get<ReceivableDetail>(`/receivables/${id}`)
}

export function fetchClientGroups() {
  return api.get<{ groups: string[] }>('/receivables/client-groups')
}

export function previewLetter(request: LetterRequest) {
  return api.post<LetterPreview>('/receivables/letters/preview', request)
}

export function sendLetter(request: LetterRequest) {
  return api.post<LetterResult>('/receivables/letters', request)
}

export function fetchSentLetter(actionId: number) {
  return api.get<SentLetter>(`/receivables/letters/${actionId}`)
}

/** The same letter again; `to` replaces the recipients when given. */
export function resendLetter(actionId: number, request: { to?: string[]; requestId: string }) {
  return api.post<LetterResult>(`/receivables/letters/${actionId}/resend`, request)
}

/** Switches the letter's online link off; the email already sent stays as it was. */
export function revokeLetterLink(actionId: number) {
  return api.post<null>(`/receivables/letters/${actionId}/link/revoke`)
}

/** The letter as a printable A4 page, with the QR to its online copy. */
export function fetchPrintLetter(actionId: number) {
  return api.get<{ title: string; html: string }>(`/receivables/letters/${actionId}/print`)
}

// --- Pengaturan surat -------------------------------------------------------

export interface LetterAccount {
  bank: string
  number: string
  branch: string
}

export interface LetterPic {
  name: string
  email: string
  phone: string
}

/** How mail is set up on the server; read-only, maintained by the infrastructure team. */
export interface MailStatus {
  enabled: boolean
  host: string | null
  port: number | null
  from: string
  pending: number
  failedLast7Days: number
  lastSentAt: string | null
}

export interface SettingsChange {
  at: string
  who: string
  summary: string
  detail: string | null
}

export interface LetterSettingsForm {
  cc: string[]
  replyEmail: string
  accounts: LetterAccount[]
  pics: LetterPic[]
  signatoryName: string
  signatoryTitle: string
  city: string
  linkTtlDays: number
}

export interface LetterSettingsView extends LetterSettingsForm {
  mail: MailStatus
  history: SettingsChange[]
}

export const settingsKey = ['receivables', 'settings'] as const

export function fetchLetterSettings() {
  return api.get<LetterSettingsView>('/receivables/settings')
}

export function saveLetterSettings(form: LetterSettingsForm) {
  return api.put<LetterSettingsView>('/receivables/settings', form)
}

export function sendTestEmail(to: string) {
  return api.post<{ sent: boolean; at: string; error: string | null }>('/receivables/settings/test-email', { to })
}

export function recordShipment(request: ShipmentRequest) {
  return api.post<{ count: number }>('/receivables/shipments', request)
}

export function updateShipment(shipmentId: number, request: ShipmentStatusRequest) {
  return api.post<null>(`/receivables/shipments/${shipmentId}/events`, request)
}

export function uploadDocuments(invoiceId: number, request: UploadRequest) {
  const form = new FormData()
  form.set('category', request.category)
  request.files.forEach((file) => form.append('files', file))
  if (request.documentDate) form.set('documentDate', request.documentDate)
  if (request.letterNo) form.set('letterNo', request.letterNo)
  if (request.note) form.set('note', request.note)
  if (request.statusCode) form.set('statusCode', request.statusCode)
  return api.upload<DocumentFile[]>(`/receivables/${invoiceId}/documents`, form)
}

export function deleteDocument(documentId: number) {
  return api.delete<null>(`/receivables/documents/${documentId}`)
}

/** The file's bytes; `inline` asks the server to let the browser show it. */
export function fetchDocument(documentId: number, inline = false) {
  return api.blob(`/receivables/documents/${documentId}?download=${inline ? 'false' : 'true'}`)
}

export function recordPayment(invoiceId: number, request: PaymentNoteRequest) {
  return api.post<null>(`/receivables/${invoiceId}/payments`, request)
}

export function setStatusCode(invoiceId: number, code: StatusCode | null, note?: string) {
  return api.put<null>(`/receivables/${invoiceId}/status`, { code, note })
}

export function updateClient(customerCode: string, request: ClientProfileRequest) {
  return api.put<null>(`/receivables/clients/${encodeURIComponent(customerCode)}`, request)
}
